import React from 'react';
import { Badge, Button, Column, Modal, Select } from '@odin-labs/components';
import {
  AccessGroupSummaryFragment,
  GetWorkerAccessGroupsQuery,
  useGetWorkerAccessGroupsQuery,
  useGrantAccessGroupMembershipMutation,
  useRevokeAccessGroupMembershipMutation,
  ZonesMode,
} from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import {
  AccessGroupsError,
  AccessGroupStatus,
  AccessGroupActionFeedback,
  GroupZones,
} from './AccessGroupStatus';
import {
  actorName,
  formatAccessDate,
  membershipSourceLabels,
  modeDescriptions,
} from './presentation';
import { AccessGroupsTable } from './AccessGroupsTable';
import { useAccessGroupAction } from './useAccessGroupAction';

type Membership =
  GetWorkerAccessGroupsQuery['workerAccessGroupMemberships'][number];
type Change =
  | { kind: 'grant'; group: AccessGroupSummaryFragment }
  | { kind: 'revoke'; membership: Membership };
const getHistoryColumns = (timeZone: string): Column<Membership>[] => [
  {
    id: 'group',
    Header: 'Group',
    accessor: (membership) =>
      `${membership.group.name}${
        membership.group.archivedAt ? ' (archived)' : ''
      }`,
  },
  {
    id: 'grant',
    Header: 'Granted',
    accessor: (membership) =>
      `${formatAccessDate(membership.grantedAt, timeZone)} · ${actorName(
        membership.grantedBy,
      )} (${membershipSourceLabels[membership.grantSource]})`,
  },
  {
    id: 'revoke',
    Header: 'Revoked',
    accessor: (membership) =>
      membership.revokedAt
        ? `${formatAccessDate(membership.revokedAt, timeZone)} · ${actorName(
            membership.revokedBy,
          )} (${
            membershipSourceLabels[membership.revokeSource] ?? 'Unknown source'
          })`
        : 'Active',
  },
];

function getMembershipColumns({
  timeZone,
  disabled,
  onRevoke,
}: {
  timeZone: string;
  disabled: boolean;
  onRevoke: (membership: Membership) => void;
}): Column<Membership>[] {
  return [
    {
      id: 'group',
      Header: 'Group',
      accessor: (membership) => membership.group.name,
      Cell: ({ row }) => (
        <div>
          {row.original.group.name}{' '}
          {row.original.group.isDefault && (
            <Badge text="Default" color="blue" />
          )}
        </div>
      ),
    },
    {
      id: 'zones',
      Header: 'Zones',
      Cell: ({ row }) => <GroupZones zones={row.original.group.zones} />,
    },
    {
      id: 'sync',
      Header: 'Group configuration',
      Cell: ({ row }) => (
        <AccessGroupStatus
          sync={row.original.group.avigilon}
          timeZone={timeZone}
        />
      ),
    },
    {
      id: 'action',
      Header: 'Membership',
      Cell: ({ row }) => (
        <Button
          text={`Revoke ${row.original.group.name}`}
          theme="naked"
          size="sm"
          disabled={disabled}
          onClick={() => onRevoke(row.original)}
        />
      ),
    },
  ];
}

export function WorkerSiteAccessGroups({
  siteId,
  workerId,
  eligibility,
  hasSiteAssignment,
  canGrant,
  onRefreshChange,
}: {
  siteId: string;
  workerId: string;
  eligibility: string;
  hasSiteAssignment: boolean;
  /**
   * Whether memberships can be granted at this site, which its site-layout
   * flag decides. Without it, a site whose zones mode is Off shows only that
   * zones are not enabled; at any other site the worker's memberships are
   * still shown, with their history, and can be revoked, so access can be
   * withdrawn.
   */
  canGrant: boolean;
  onRefreshChange?: (refresh: () => Promise<unknown>) => void;
}): React.ReactElement {
  const grantAllowed = hasSiteAssignment && canGrant;
  const { data, loading, error, refetch } = useGetWorkerAccessGroupsQuery({
    variables: { siteId, workerId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const [groupId, setGroupId] = React.useState<string | null>(null);
  const [change, setChange] = React.useState<Change | null>(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const [grant] = useGrantAccessGroupMembershipMutation();
  const [revoke] = useRevokeAccessGroupMembershipMutation();
  const action = useAccessGroupAction(refetch);
  React.useEffect(() => onRefreshChange?.(refetch), [onRefreshChange, refetch]);
  const timeZone = data?.getJobsite.timeZone ?? 'UTC';
  const historyColumns = React.useMemo(
    () => getHistoryColumns(timeZone),
    [timeZone],
  );
  const memberships = data?.workerAccessGroupMemberships ?? [];
  const activeMemberships = memberships.filter(
    (membership) => !membership.revokedAt && !membership.group.archivedAt,
  );
  const activeIds = new Set(
    activeMemberships.map((membership) => membership.group.id),
  );
  const available = (data?.getJobsite.accessGroups ?? []).filter(
    (group) => !group.archivedAt && !activeIds.has(group.id),
  );
  const options = available.map((group) => ({
    value: group.id,
    label: `${group.name}${group.isDefault ? ' (default)' : ''}`,
  }));
  const selectedGroup = available.find((group) => group.id === groupId);
  const openRevoke = React.useCallback(
    (selected: Membership) =>
      setChange({ kind: 'revoke', membership: selected }),
    [],
  );
  const columns = React.useMemo(
    () =>
      getMembershipColumns({
        timeZone,
        disabled: action.disabled || loading,
        onRevoke: openRevoke,
      }),
    [action.disabled, loading, openRevoke, timeZone],
  );
  const changeStillApplies =
    change?.kind === 'grant'
      ? grantAllowed && available.some((group) => group.id === change.group.id)
      : change?.kind === 'revoke' &&
        activeMemberships.some(
          (membership) => membership.id === change.membership.id,
        );
  React.useEffect(() => {
    if (change && !loading && !error && !changeStillApplies) {
      setChange(null);
      setGroupId(null);
    }
  }, [change, changeStillApplies, loading, error]);
  const confirm = async (): Promise<void> => {
    if (!change || !changeStillApplies || action.disabled || loading || error)
      return;
    const accessGroupId =
      change.kind === 'grant' ? change.group.id : change.membership.group.id;
    const variables = { input: { siteId, workerId, accessGroupId } };
    if (
      await action.execute(
        () =>
          change.kind === 'grant'
            ? grant({ variables })
            : revoke({
                variables: {
                  input: {
                    ...variables.input,
                    expectedMembershipId: change.membership.id,
                  },
                },
              }),
        `${
          change.kind === 'grant' ? 'Membership granted' : 'Membership revoked'
        } in Odin. Hardware reconciliation is separate.`,
      )
    ) {
      setChange(null);
      setGroupId(null);
    }
  };
  if (error)
    return (
      <>
        <AccessGroupActionFeedback action={action} />
        <AccessGroupsError
          retry={() => {
            void action.retryRefresh();
          }}
        />
      </>
    );
  if (!data?.getJobsite)
    return (
      <div role="status" aria-label="Loading memberships">
        <LoadingError loading />
      </div>
    );
  if (!canGrant && data.getJobsite.zonesMode === ZonesMode.Off)
    return <p>Zones are not enabled for this site.</p>;
  return (
    <div className="odin-space-y-6" aria-busy={loading}>
      {loading && (
        <p
          role="status"
          aria-label="Refreshing memberships"
          className="odin-text-sm odin-text-gray-500"
        >
          Refreshing memberships…
        </p>
      )}
      <div className="odin-rounded-lg odin-bg-gray-50 odin-p-4 odin-text-sm odin-space-y-2">
        <p>{modeDescriptions[data.getJobsite.zonesMode]}</p>
        <p>{eligibility}</p>
        <p>
          Memberships are configured entitlements. A saved membership or synced
          group does not confirm this worker’s hardware access.
        </p>
      </div>
      <AccessGroupActionFeedback action={action} />
      {canGrant && !hasSiteAssignment && (
        <p className="odin-text-sm">
          Assign this worker to the site before granting a membership. You can
          still revoke memberships and view their history.
        </p>
      )}
      {canGrant ? (
        <div className="odin-flex odin-items-end odin-gap-3">
          <Select
            className="odin-flex-1"
            name="membership-group"
            instanceId="membership-group"
            inputId="membership-group"
            label="Access group to grant"
            options={options}
            value={options.find((option) => option.value === groupId) ?? null}
            onChange={(option) => setGroupId(option?.value ?? null)}
            isDisabled={!hasSiteAssignment || action.disabled || loading}
            noOptionsMessage={() => 'No additional groups available'}
          />
          <Button
            text="Grant membership"
            disabled={
              !hasSiteAssignment || !selectedGroup || action.disabled || loading
            }
            onClick={() => {
              if (grantAllowed && selectedGroup)
                setChange({ kind: 'grant', group: selectedGroup });
            }}
          />
        </div>
      ) : (
        <p className="odin-text-sm">
          Granting memberships isn&apos;t available at this site yet. You can
          still revoke memberships and view their history.
        </p>
      )}
      <section aria-label="Current memberships" className="odin-space-y-3">
        <h3 className="odin-font-semibold">
          Current memberships ({activeMemberships.length})
        </h3>
        <AccessGroupsTable
          enablePagination
          columns={columns}
          data={activeMemberships}
          noResultsText="No active memberships at this site. Default membership is not implied."
          disableGlobalFilter
        />
      </section>
      <Button
        text={
          showHistory ? 'Hide membership history' : 'Show membership history'
        }
        theme="secondary"
        aria-expanded={showHistory}
        onClick={() => setShowHistory((shown) => !shown)}
      />
      {showHistory && (
        <section aria-label="Membership history" className="odin-space-y-3">
          <h3 className="odin-font-semibold">Membership history</h3>
          <AccessGroupsTable
            enablePagination
            columns={historyColumns}
            data={memberships}
            noResultsText="No membership history at this site"
            disableGlobalFilter
          />
        </section>
      )}
      {change && (
        <Modal
          open
          title={
            change.kind === 'grant'
              ? `Grant ${change.group.name}?`
              : `Revoke ${change.membership.group.name}?`
          }
          size="sm"
          setOpen={(open) => {
            if (!open && !action.busy) setChange(null);
          }}
          actionText={
            change.kind === 'grant' ? 'Confirm grant' : 'Confirm revoke'
          }
          actionButtonTheme={change.kind === 'grant' ? 'primary' : 'danger'}
          onAction={confirm}
          actionButtonEnabled={
            !action.disabled && !loading && Boolean(changeStillApplies)
          }
          actionButtonWithSpinner={action.busy}
          cancelButtonEnabled={!action.busy}
        >
          <p className="odin-mb-4 odin-text-sm">
            {change.kind === 'grant'
              ? 'This saves a membership for this worker at the selected site. Site eligibility and provider reconciliation still determine hardware access.'
              : `This closes the membership and retains its history.${
                  change.membership.group.isDefault
                    ? ' Default membership will not be automatically granted again.'
                    : ''
                } Provider reconciliation follows separately.`}
          </p>
          <AccessGroupActionFeedback action={action} />
        </Modal>
      )}
    </div>
  );
}
